/** Shared storage for dismissed alert ids (client-only). */
export const DISMISSED_ALERTS_KEY = "ht_dismissed_alerts_v1";

export function getDismissedAlertIds() {
  try {
    const raw = localStorage.getItem(DISMISSED_ALERTS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? new Set(arr.map(String)) : new Set();
  } catch {
    return new Set();
  }
}

export function dismissAlertId(id) {
  const set = getDismissedAlertIds();
  set.add(String(id));
  localStorage.setItem(DISMISSED_ALERTS_KEY, JSON.stringify([...set]));
}

export function undismissAllAlerts() {
  localStorage.removeItem(DISMISSED_ALERTS_KEY);
}

function num(metrics, key) {
  const m = metrics?.[key];
  if (!m || m.value === undefined || m.value === null) return null;
  if (typeof m.value === "number" && !Number.isNaN(m.value)) return m.value;
  const p = parseFloat(m.value);
  return Number.isNaN(p) ? null : p;
}

function formatRelativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 14) return `${days} day${days !== 1 ? "s" : ""} ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Build actionable alerts from user profile reports (newest-first analysis).
 * @param {object|null} profile - API user object with reports[]
 * @param {Set<string>} dismissed
 * @returns {Array<{ id: string, title: string, description: string, time: string, type: 'danger' | 'warning' | 'info' }>}
 */
export function buildAlertsFromProfile(profile, dismissed = new Set()) {
  const reports = profile?.reports || [];
  if (!reports.length) return [];

  const sorted = [...reports].sort((a, b) => {
    const ta = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
    const tb = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
    return tb - ta;
  });

  const latest = sorted[0];
  const m = latest.extractedData?.metrics || {};
  const alerts = [];
  const tLatest = latest.uploadedAt;

  const hba1c = num(m, "hba1c");
  if (hba1c != null && hba1c >= 6.5) {
    alerts.push({
      id: "hba1c-diabetes",
      title: "HbA1c in diabetes range",
      description: `Latest HbA1c is ${hba1c}%. Discuss targets and follow-up testing with your clinician.`,
      time: formatRelativeTime(tLatest),
      type: "danger"
    });
  } else if (hba1c != null && hba1c >= 5.7) {
    alerts.push({
      id: "hba1c-prediabetes",
      title: "HbA1c suggests prediabetes",
      description: `HbA1c ${hba1c}% is often classified as prediabetes. Lifestyle changes and monitoring may help.`,
      time: formatRelativeTime(tLatest),
      type: "warning"
    });
  }

  const ldl = num(m, "ldl");
  if (ldl != null && ldl > 160) {
    alerts.push({
      id: "ldl-high",
      title: "LDL cholesterol elevated",
      description: `LDL is ${ldl} mg/dL. Your provider can interpret this with your overall cardiac risk.`,
      time: formatRelativeTime(tLatest),
      type: "warning"
    });
  }

  const vitD = num(m, "vitaminD");
  if (vitD != null && vitD < 25) {
    alerts.push({
      id: "vitd-low",
      title: "Vitamin D below common range",
      description: `Level ${vitD} ng/mL may be low. Ask about supplementation and retesting.`,
      time: formatRelativeTime(tLatest),
      type: "danger"
    });
  } else if (vitD != null && vitD < 30) {
    alerts.push({
      id: "vitd-borderline",
      title: "Vitamin D on the low side",
      description: `Level ${vitD} ng/mL is under many sufficiency cutoffs. Worth reviewing with your clinician.`,
      time: formatRelativeTime(tLatest),
      type: "warning"
    });
  }

  const tsh = num(m, "tsh");
  if (tsh != null && (tsh < 0.4 || tsh > 4.5)) {
    alerts.push({
      id: "tsh-out-of-range",
      title: "TSH outside typical reference",
      description: `TSH ${tsh} mIU/L should be interpreted with symptoms and prior thyroid history.`,
      time: formatRelativeTime(tLatest),
      type: "warning"
    });
  }

  const trig = num(m, "triglycerides");
  if (trig != null && trig > 200) {
    alerts.push({
      id: "trig-high",
      title: "Triglycerides elevated",
      description: `Triglycerides ${trig} mg/dL are above a common threshold. Diet, alcohol, and meds affect this.`,
      time: formatRelativeTime(tLatest),
      type: "warning"
    });
  }

  if (sorted.length > 1) {
    const older = sorted[1];
    const mOld = older.extractedData?.metrics || {};
    const h1 = num(m, "hba1c");
    const h0 = num(mOld, "hba1c");
    if (h1 != null && h0 != null && h1 < h0 - 0.15) {
      alerts.push({
        id: "hba1c-trend-down",
        title: "HbA1c trending down",
        description: `Compared to your prior report, HbA1c improved (${h0}% → ${h1}%). Keep up the plan your team recommends.`,
        time: formatRelativeTime(tLatest),
        type: "info"
      });
    }
  }

  if (!alerts.length) {
    alerts.push({
      id: "all-clear-latest",
      title: "No urgent flags in latest labs",
      description:
        "We did not detect common threshold alerts in your most recent upload. Continue routine care and uploads after new tests.",
      time: formatRelativeTime(tLatest),
      type: "info"
    });
  }

  return alerts.filter((a) => !dismissed.has(a.id));
}
